import { useCallback, useMemo, useState } from 'react'

import { fetchPartnerLnRStatus } from 'clickNgo/entities/partner/api'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useSafetyTranslation } from 'cng-common/features/translations'
import { Loader } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'

import { CockpitDrawer } from '../../../components/cockpitDrawer'
import { CockpitEmptyState } from '../../../components/cockpitEmptyState/CockpitEmptyState'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { usePartnerCompanyDetails } from '../../hooks/usePartnerCompanyDetails'

import {
  CompanyDetailsActions,
  CompanyDetailsHeader,
  CompanyDetailsItem,
  PartnerStatusModal,
} from './components'
import { CompanyDetailsFlags } from './components/companyDetailsFlags'
import { CompanyDetailsStatus } from './components/companyDetailsStatus'
import { getCompanyAddress, getCompanyContractType } from './helpers'
import { companyDetailsTranslations } from './translation'

import styles from './PartnerExtendedInfo.module.scss'

/** Id of the drawer, for the toggle that opens it to point at. */
export const PARTNER_EXTENDED_INFO_ID = 'cockpit-partner-extended-info'

interface IPartnerExtendedInfoProps {
  /** The partner whose company details the drawer shows. */
  partnerId: string
  isOpen: boolean
  onClose: () => void
  /** The page area the drawer opens over. */
  getHost: () => HTMLElement | null | undefined
}

/** The partner's company details, in a drawer at the left edge of the cockpit, worn like a tile:
 *  once the details are in, the drawer's header is the partner's own, with its actions and the
 *  close button on the right; the details stand as small labelled facts under it.
 *  Read-only: the cockpit plans the partner, it does not keep the partner's master data. */
export const PartnerExtendedInfo = ({
  partnerId,
  isOpen,
  onClose,
  getHost,
}: IPartnerExtendedInfoProps) => {
  const { isLoading, partner, partnerFlags, partnerAttachments } = usePartnerCompanyDetails(
    partnerId,
    isOpen
  )
  const t = useSafetyTranslation()
  const [isPartnerStatusModalOpen, setIsPartnerStatusModalOpen] = useState(false)
  const loadedPartnerId = partner?.partnerId
  /* Asked for once the partner is in, and kept while the drawer closes and opens again. */
  const lnrStatus = useMemo(async () => {
    if (!loadedPartnerId) {
      return '-'
    }

    const reportDetails = await fetchPartnerLnRStatus(loadedPartnerId)
    return reportDetails?.approvalStatus || '-'
  }, [loadedPartnerId])
  const openPartnerStatusModal = useCallback(() => setIsPartnerStatusModalOpen(true), [])
  const closePartnerStatusModal = useCallback(() => setIsPartnerStatusModalOpen(false), [])
  /* The status modal goes with the drawer, so it does not stand open again when the drawer does. */
  const closeDrawer = useCallback(() => {
    setIsPartnerStatusModalOpen(false)
    onClose()
  }, [onClose])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.state}>
          <Loader dimension={{ width: 40, height: 40 }} />
        </div>
      )
    }

    if (!partner || !partnerFlags) {
      return (
        <div className={styles.state}>
          <CockpitEmptyState
            icon="kbase kbase-info-alt"
            title="Partner details are not available"
          />
        </div>
      )
    }

    const agreements = partnerFlags.agreement.map(({ code }) => code) || []
    const companyAddress = getCompanyAddress(partner)
    const companyContractType = getCompanyContractType(partner.partnerType)
    const hpMemberSince = partner.hpMemberSince && getFormatDate(partner.hpMemberSince, 'date')
    const hpPartnerTypeStartDate =
      partner.partnerTypeStartDate && getFormatDate(partner.partnerTypeStartDate, 'date')

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.block}>
            <Tooltip tipContent={() => <>{companyAddress}</>}>
              <div className={styles.companyAddress}>{companyAddress}</div>
            </Tooltip>
          </div>
          <div className={styles.block}>
            <CompanyDetailsFlags
              partnerId={partner.partnerId}
              isAmplifyMember={partner.isAmplifyMember}
              agreements={agreements}
              specialPricingFlag={partner.splPriceFlag}
            />
          </div>
          <hr />
          <dl className={styles.facts}>
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.hpInAgrimentWithHP}
              value={hpMemberSince}
            />
            <RenderWhen condition={Boolean(companyContractType)}>
              <CompanyDetailsItem label={companyContractType} value={hpPartnerTypeStartDate} />
            </RenderWhen>
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.lnrStatus}
              value={lnrStatus}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.pricingType}
              value={partner.resellerAbInd}
            />
          </dl>
          {/* In a block of its own, so the chip keeps its own width rather than the column's. */}
          <div className={styles.partnerStatusButton}>
            <CockpitIconButton
              showLabel
              icon="kbase kbase-chart"
              label={t(companyDetailsTranslations.actions.partnerStatusButton)}
              onClick={openPartnerStatusModal}
            />
          </div>
          <div className={styles.block}>
            <CompanyDetailsStatus partner={partner} partnerFlags={partnerFlags} />
          </div>
          <PartnerStatusModal
            partnerId={partner.partnerId}
            isOpen={isPartnerStatusModalOpen}
            onClose={closePartnerStatusModal}
          />
          <hr />
          <dl className={styles.facts}>
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.market}
              value={partner.market}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.organization}
              value={partner.organization}
            />
          </dl>
          <hr />
          <dl className={styles.facts}>
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.taxId}
              value={partner.taxId}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.parentPartnerId}
              value={partner.parentPartnerId}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.partnerId}
              value={partner.partnerId}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.locationId}
              value={partner.locationId}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.reporterId}
              value={partner.reporterId}
            />
            <CompanyDetailsItem
              label={companyDetailsTranslations.details.storeNumber}
              value={partner.storeNumber}
            />
          </dl>
        </div>
      </div>
    )
  }

  return (
    <CockpitDrawer
      id={PARTNER_EXTENDED_INFO_ID}
      side="left"
      className={styles.drawer}
      /* No title while the details load: the partner's header takes the bar once they are in. */
      title={isLoading ? null : 'Extended info'}
      renderHeader={
        partner && partnerFlags
          ? (closeButton) => (
              <CompanyDetailsHeader
                partner={partner}
                partnerFlags={partnerFlags}
                actions={
                  <>
                    <CompanyDetailsActions
                      partner={partner}
                      partnerFlags={partnerFlags}
                      partnerAttachments={partnerAttachments}
                      isReadOnly
                    />
                    {closeButton}
                  </>
                }
              />
            )
          : undefined
      }
      ariaLabel="Extended info"
      closeLabel="Close extended info"
      isOpen={isOpen}
      onClose={closeDrawer}
      getHost={getHost}
    >
      {renderContent()}
    </CockpitDrawer>
  )
}

PartnerExtendedInfo.displayName = 'PartnerExtendedInfo'
